import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { X, Bell, CheckCircle2, LogOut } from 'lucide-react';
import { selectUser } from 'redux/slices/authSlice';
import { performLogout } from '@/utils/logout';
import {
    useGetNotificationsQuery,
    useUpdateNotificationsMutation,
} from 'redux/services/notifications';
import { buildAppPath } from '@/utils/tenant';
import AccountQuickPanel from '@/components/account/AccountQuickPanel';
import styles from './MobileHeader.module.css';

const getInitials = (name = '') =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join('') || 'MU';

export default function MobileHeader() {
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const [panel, setPanel] = useState(null); // 'notifications' | 'profile' | null
    const [showUnread, setShowUnread] = useState(false);

    const { data: notificationData, isFetching } = useGetNotificationsQuery(
        { showUnread },
        { refetchOnMountOrArgChange: 300 }
    );
    const [updateNotification] = useUpdateNotificationsMutation();

    const notifications = notificationData?.data?.data || [];
    const unreadCount = notifications.filter((n) => n?.isRead === '0').length;

    const displayName =
        user?.name ||
        [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
        user?.email ||
        'Mira User';
    const initials = getInitials(displayName);
    const avatarUrl =
        user?.profile_picture_url ||
        user?.profilePictureUrl ||
        user?.avatar_url ||
        user?.profile_picture ||
        '';

    const markRead = async (notification) => {
        if (!notification || notification?.isRead !== '0') return;
        await updateNotification({
            data: { notification_ids: [notification.userNotificationId] },
        });
    };

    const handleLogout = async () => {
        setPanel(null);
        await performLogout({ redirect: true });
    };

    useEffect(() => {
        const handleOpenNotifications = () => {
            setPanel('notifications');
        };
        window.addEventListener('mira:open-notifications', handleOpenNotifications);
        return () => window.removeEventListener('mira:open-notifications', handleOpenNotifications);
    }, []);

    return (
        <>
            {/* ── Top Header Bar ─────────────────────────────────────── */}
            <header className={styles.header}>
                <div className={styles.left}>
                    <img src="/loaninghub-icon.png" alt="Logo" className={styles.logo} />
                    <span className={styles.title}>Loaning Hub</span>
                </div>

                <div className={styles.actions}>
                    {/* Notification Bell */}
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${panel === 'notifications' ? styles.iconBtnActive : ''}`}
                        onClick={() => setPanel(panel === 'notifications' ? null : 'notifications')}
                        aria-label="Notifications"
                    >
                        <Bell className={styles.icon} />
                        {unreadCount > 0 && <span className={styles.dot} />}
                    </button>

                    {/* Profile Avatar (hidden for consumers who have it in bottom nav) */}
                    {user?.role !== 'consumer' && (
                        <button
                            type="button"
                            className={`${styles.avatarBtn} ${panel === 'profile' ? styles.avatarBtnActive : ''}`}
                            onClick={() => setPanel(panel === 'profile' ? null : 'profile')}
                            aria-label="Profile"
                        >
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="" className={styles.avatarImage} />
                            ) : (
                                <span className={styles.avatarInitials}>{initials}</span>
                            )}
                        </button>
                    )}
                </div>
            </header>

            {/* ── Overlay Backdrop ────────────────────────────────────── */}
            {panel && (
                <div className={styles.backdrop} onClick={() => setPanel(null)} />
            )}

            {/* ── Notification Panel (slide down from header) ──────── */}
            {panel === 'notifications' && (
                <div className={styles.notifPanel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelHeaderLeft}>
                            <Bell className={styles.panelHeaderIcon} />
                            <div>
                                <p className={styles.panelTitle}>Notifications</p>
                                <p className={styles.panelSubtitle}>Stay updated on important events</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={() => setPanel(null)}
                        >
                            <X className={styles.closeBtnIcon} />
                        </button>
                    </div>

                    <div className={styles.notifControls}>
                        <label className={styles.unreadToggle}>
                            <input
                                type="checkbox"
                                checked={showUnread}
                                onChange={(e) => setShowUnread(e.target.checked)}
                            />
                            <span>Show unread only</span>
                        </label>
                    </div>

                    <div className={styles.notifList}>
                        {isFetching && <p className={styles.emptyMsg}>Loading notifications...</p>}
                        {!isFetching && notifications.length === 0 && (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyIconWrap}>
                                    <Bell className={styles.emptyIcon} />
                                </div>
                                <p className={styles.emptyTitle}>No new notifications</p>
                                <p className={styles.emptyHint}>You are all caught up for now.</p>
                            </div>
                        )}
                        {!isFetching &&
                            notifications.map((notif, i) => (
                                <div key={notif.userNotificationId || i} className={styles.notifItem}>
                                    <div className={styles.notifAvatar}>
                                        {notif.senderName?.[0]?.toUpperCase() || 'N'}
                                    </div>
                                    <div className={styles.notifBody}>
                                        <div className={styles.notifMeta}>
                                            <p className={styles.notifTitle}>{notif.subject || 'Notification'}</p>
                                            {notif.isRead === '0' && <span className={styles.unreadDot} />}
                                        </div>
                                        <p className={styles.notifMsg}>{notif.message || 'You have a new update.'}</p>
                                        {notif.isRead === '0' && (
                                            <button
                                                type="button"
                                                className={styles.markReadBtn}
                                                onClick={() => markRead(notif)}
                                            >
                                                Mark as read
                                                <CheckCircle2 className={styles.markReadIcon} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {/* ── Profile Sheet (slides up from bottom) ────────────── */}
            {panel === 'profile' && (
                <div className={styles.profileSheet}>
                    <div className={styles.sheetHandle} />
                    <AccountQuickPanel
                        user={user}
                        mode="mobile"
                        onClose={() => setPanel(null)}
                        onNotifications={() => {
                            setPanel('notifications');
                        }}
                        onSupport={() => {
                            setPanel(null);
                            navigate(buildAppPath('/help-support'));
                        }}
                    />
                    <button type="button" className={styles.signOutBtn} onClick={handleLogout}>
                        <LogOut className={styles.signOutIcon} />
                        <span>Sign out</span>
                    </button>
                </div>
            )}
        </>
    );
}
